import { Camera } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ImagePlaceholderProps {
  /** 'compact' for list rows (small), 'card' for grid cards, 'detail' for full-size views */
  variant?: 'compact' | 'card' | 'detail';
  /** Show as interactive "Add Image" CTA */
  interactive?: boolean;
  className?: string;
}

/**
 * Unified image placeholder with subtle depth and transparency.
 * Context-aware sizing based on variant prop.
 */
export function ImagePlaceholder({ 
  variant = 'card', 
  interactive = false,
  className 
}: ImagePlaceholderProps) {
  const isCompact = variant === 'compact';
  const isDetail = variant === 'detail';
  
  return (
    <div 
      className={cn(
        // Base: subtle gradient background with transparency
        'flex flex-col items-center justify-center',
        'bg-gradient-to-br from-white/[0.03] to-transparent',
        'border border-white/[0.06]',
        
        // Subtle inner glow for depth
        'shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]',
        
        // Variant-specific sizing
        isCompact && 'w-10 h-10 rounded-md gap-0',
        variant === 'card' && 'aspect-square rounded-lg gap-1',
        isDetail && 'aspect-square rounded-xl gap-1.5',
        
        // Interactive states
        interactive && 'group/placeholder cursor-pointer hover:bg-white/[0.04] hover:border-white/[0.1] transition-all duration-200',
        
        className
      )}
    >
      {interactive ? (
        <>
          <Camera 
            className={cn(
              'text-muted-foreground/40',
              isCompact && 'w-3.5 h-3.5',
              variant === 'card' && 'w-6 h-6',
              isDetail && 'w-8 h-8',
              'group-hover/placeholder:text-muted-foreground/60 transition-colors'
            )} 
          />
          {!isCompact && (
            <span className={cn(
              'text-muted-foreground/40 font-medium',
              variant === 'card' && 'text-[10px]',
              isDetail && 'text-xs',
              'group-hover/placeholder:text-muted-foreground/60 transition-colors'
            )}>
              Add Photo
            </span>
          )}
        </>
      ) : (
        <span 
          className={cn(
            'text-muted-foreground/50 font-medium',
            isCompact && 'text-[9px] tracking-wide',
            variant === 'card' && 'text-[11px]',
            isDetail && 'text-sm',
          )}
        >
          {isDetail ? 'No photos added' : 'N/A'}
        </span>
      )}
    </div>
  );
}
